import React from 'react'
import classNames from 'classnames'
import { Image } from '@/atoms/Image'
import { InternalLink } from '@/atoms/InternalLink'
import { ParagraphSM } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { BeforeYouLeaveProvider } from '@/contexts/BeforeYouLeaveContext'
import { BeforeYouLeaveModal } from '@/molecules/BeforeYouLeaveModal'
import { AngelFundingLink } from '@/molecules/InvestLink'
import { Gravity } from '@/utils/Cloudinary'
import { useSafeAnalytics } from '@/utils/analytics'

export interface BannerProps {
  className?: string
  bannerClassName?: string
  imageClassName?: string
  wrapperClassName?: string
  banner: BannerViewModel
}

export interface BannerViewModel {
  id: string
  backgroundCloudinaryPath: string
  tabletBackgroundCloudinaryPath?: string | null
  mobileBackgroundCloudinaryPath?: string | null
  titleImage?: {
    cloudinaryPath: string
    height: number
    width: number
    alt?: string
  }
  descriptionText?: string
  href?: string
  backgroundStyle?: Record<string, string>
  logoStyle?: Record<string, string>
  gravity?: Gravity | null
}

/**
 * adds a gradient to that is weighted to the bottom-left to allow for words and
 * logos to stand out more without needing a reduced opacity for the whole image
 * gradient came from figma
 */
const gradient = `linear-gradient(82.79deg, rgba(0, 0, 0, 0.5) 0%, rgba(0, 0, 0, 0) 55.61%), linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.25) 100%)`

export const Banner: React.FC<BannerProps> = ({
  banner,
  className,
  bannerClassName,
  imageClassName,
  wrapperClassName,
}) => {
  const { track } = useSafeAnalytics()
  const content = (
    <div
      data-testid={`banner-${banner.id}`}
      className={classNames(
        'banner-content-container relative w-full cursor-pointer rounded-xl border-4 border-transparent leading-[0] transition-all duration-500 hover:border-white',
        wrapperClassName,
      )}
    >
      <div
        className={classNames('-m-[1px] hidden rounded-lg 2xl:block', bannerClassName)}
        style={{ background: gradient }}
      >
        <Image
          alt={banner.titleImage?.alt}
          src={`ar_20:5,c_crop,${banner.gravity ?? 'g_center'}/${banner.backgroundCloudinaryPath}`}
          width={4800}
          height={2057}
          className={classNames('-z-10 rounded-lg', imageClassName)}
          style={banner.backgroundStyle}
          priority
        />
      </div>
      <div
        className={classNames('-m-[1px] hidden rounded-lg lg:block 2xl:hidden', bannerClassName)}
        style={{ background: gradient }}
      >
        <Image
          alt={banner.titleImage?.alt}
          src={`ar_20:5,c_crop,${banner.gravity ?? 'g_center'}/${banner.backgroundCloudinaryPath}`}
          width={2000}
          height={500}
          className={classNames('-z-10 rounded-lg', imageClassName)}
          style={banner.backgroundStyle}
          priority
        />
      </div>
      {/* Mobile Banner Image */}
      <div className={classNames('-m-[1px] rounded-lg md:hidden', bannerClassName)} style={{ background: gradient }}>
        <Image
          alt={banner.titleImage?.alt}
          src={
            banner?.mobileBackgroundCloudinaryPath && !banner?.mobileBackgroundCloudinaryPath.endsWith('undefined.webp')
              ? banner?.mobileBackgroundCloudinaryPath
              : banner.backgroundCloudinaryPath
          }
          width={1400}
          height={600}
          className={classNames('-z-10 min-h-full rounded-lg object-cover', imageClassName)}
          style={banner.backgroundStyle}
        />
      </div>
      {/* Tablet Banner Image */}
      <div
        className={classNames('-m-[1px] hidden md:block rounded-lg lg:hidden', bannerClassName)}
        style={{ background: gradient }}
      >
        <Image
          alt={banner.titleImage?.alt}
          src={
            banner?.tabletBackgroundCloudinaryPath && !banner?.tabletBackgroundCloudinaryPath.endsWith('undefined.webp')
              ? banner?.tabletBackgroundCloudinaryPath
              : banner?.mobileBackgroundCloudinaryPath &&
                !banner?.mobileBackgroundCloudinaryPath.endsWith('undefined.webp')
              ? banner?.mobileBackgroundCloudinaryPath
              : banner.backgroundCloudinaryPath
          }
          width={1400}
          height={600}
          className={classNames('-z-10 rounded-lg object-cover min-h-full', imageClassName)}
          style={banner.backgroundStyle}
        />
      </div>
      {banner.titleImage && (
        <div className="title-image absolute left-0 top-0 p-4 sm:pl-6 md:pl-8" style={banner.logoStyle}>
          <Image
            alt={banner.titleImage.alt}
            src={banner.titleImage.cloudinaryPath}
            width={banner.titleImage.width}
            height={banner.titleImage.height}
          />
        </div>
      )}
      <div className="description-text absolute inset-x-4 bottom-4 sm:inset-x-6 sm:bottom-6 md:inset-x-8 md:bottom-8">
        <ParagraphSM className="sm:photon-paragraph lg:photon-paragraph-xl 3xl:photon-heading-lg sm:font-semibold lg:font-bold">
          {banner.descriptionText}
        </ParagraphSM>
      </div>
    </div>
  )

  return banner?.href?.startsWith(paths.external.invest.index) ? (
    <BeforeYouLeaveProvider>
      <BeforeYouLeaveModal />
      <AngelFundingLink href={banner.href} linkContext="banner">
        {content}
      </AngelFundingLink>
    </BeforeYouLeaveProvider>
  ) : banner.href ? (
    <InternalLink
      className={classNames('banner cursor-pointer block', className)}
      href={banner.href}
      onClick={() => track('Clicked Ultrawide Banner', { href: banner.href, descriptionText: banner.descriptionText })}
    >
      {content}
    </InternalLink>
  ) : (
    <div className={classNames('banner cursor-pointer block', className)}>{content}</div>
  )
}
